import React, { useState } from "react";
import {
    Box,
    Typography,
    Grid,
    Button,
    IconButton,
    RadioGroup,
    FormControlLabel,
    Radio,
    Divider,
    FormControl,
    FormHelperText,
    CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMutation } from "@tanstack/react-query";
import { SalesArchitectureMasterclass } from "../../../../api/services";
import BoxInput from "../../../../Components/BoxInput"
import { toast } from "react-toastify";
const primary = "#2563eb";

interface Props {
    onClose: () => void;
}

interface FormValues {
    name: string;
    email: string;
    phone: string;
    companyType: string;
    teamSize: string;
    salesProcess: string;
    revenueLeak: string;
    goal: string;
    ready: string;
}
const schema: yup.ObjectSchema<FormValues> = yup.object({
    name: yup.string().required("Name is required"),
    email: yup.string().email("Invalid email").required("Email is required"),
    phone: yup.string().required("Phone is required"),
    companyType: yup.string().required("Company type is required"),
    teamSize: yup
        .string()
        .oneOf(["Solo", "2-5", "5-15", "15+"])
        .required("Team size is required"),
    salesProcess: yup
        .string()
        .oneOf(["Yes (Documented & Used)", "Informal", "No"])
        .required("Sales process selection required"),
    revenueLeak: yup
        .string()
        .oneOf([
            "Lead qualification",
            "Discovery conversations",
            "Objection handling",
            "Follow-ups",
            "Negotiation",
        ])
        .required("Revenue leak area required"),
    goal: yup.string().required("Primary goal is required"),
    ready: yup
        .string()
        .oneOf(["Yes", "Learning first"])
        .required("Selection required"),
});

const SalesArchitectureMasterclassForm: React.FC<Props> = ({
    onClose,
}) => {
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            name: "",
            email: "",
            phone: "",
            companyType: "",
            teamSize: "",
            salesProcess: "",
            revenueLeak: "",
            goal: "",
            ready: "",
        },
    });

    const mutation = useMutation({
        mutationFn: SalesArchitectureMasterclass,
        onSuccess: () => {
            toast.success("Registration Submitted Successfully");
            reset();
            onClose();
        },
        onError: (error: any) => {
            const errorMessage = error?.response?.data?.message;
            if (Array.isArray(errorMessage)) {
                // Show first validation error
                toast.error(errorMessage[0] || "Submission failed");
            } else if (typeof errorMessage === "string") {
                toast.error(errorMessage);
            } else {
                toast.error(error?.message || "Submission failed. Please try again.");
            }
        },
    });

    const onSubmit = (data: FormValues) => {
        mutation.mutate({
            name: data.name,
            email: data.email,
            phoneNumber: data.phone,
            companyType: data.companyType,
            teamSize: data.teamSize as any,
            structuredSalesProcess: data.salesProcess as any,
            revenueLeakArea: data.revenueLeak as any,
            primaryGoal: data.goal,
            readyToImplement: data.ready as any,
        });
    };

  const cleanField = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    backgroundColor: "#ffffff",

    // ✅ USER TEXT COLOR (optional but recommended)
    "& input": {
      color: "#000",
    },
    "& textarea": {
      color: "#000",
    },

    // ✅ PLACEHOLDER COLOR
    "& input::placeholder": {
      color: "#9e9e9e",
      opacity: 1, // IMPORTANT
    },
    "& textarea::placeholder": {
      color: "#9e9e9e",
      opacity: 1,
    },

    "& fieldset": {
      borderColor: "rgba(15,99,165,0.15)",
    },
    "&:hover fieldset": {
      borderColor: "#0f63a5",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0f63a5",
      boxShadow: "0 0 0 4px rgba(15,99,165,0.08)",
    },
  },
};

    return (
        <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{
                p: { xs: 3, md: 6 },
                borderRadius: "24px",
                background: "#ffffff",
                boxShadow: "0 30px 80px rgba(0,0,0,0.08)",
                position: "relative",
            }}
        >
            <IconButton
                onClick={onClose}
                sx={{ position: "absolute", top: 20, right: 20 }}
            >
                <CloseIcon />
            </IconButton>

            <Typography variant="h4" fontWeight={700} mb={1}>
                Sales Architecture Masterclass
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={5}>
                Online Implementation Workshop – System Builders
            </Typography>

            <Grid container spacing={4}>
                {/* LEFT SIDE */}
                <Grid item xs={12} md={6}>

                    <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                {...field}
                                placeholder="Full Name"
                                fullWidth
                                error={!!errors.name}
                                helperText={errors.name?.message}
                                sx={{ ...cleanField }}
                            />
                        )}
                    />
                    <Controller
                        name="phone"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                {...field}
                                placeholder="Phone Number"
                                fullWidth
                                error={!!errors.phone}
                                helperText={errors.phone?.message}
                                sx={{ mt: 2, ...cleanField }}
                            />
                        )}
                    />


                    <Typography mt={3} fontWeight={600} variant="h6" sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }}>
                        Team Size*
                    </Typography>

                    <Controller
                        name="teamSize"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.teamSize}>
                                <RadioGroup {...field}>
                                    <FormControlLabel
                                        value="Solo"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                Solo
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="2-5"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                2-5
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="5-15"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                5-15
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="15+"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                15+
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>
                                <FormHelperText>
                                    {errors.teamSize?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />

                    <Typography variant="h6" sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }} fontWeight={600}>
                        Where do you see revenue leaking most?*
                    </Typography>
                    <Controller
                        name="revenueLeak"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.revenueLeak}>
                                <RadioGroup {...field}>
                                    <FormControlLabel
                                        value="Lead qualification"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                Lead qualification
                                            </Typography>
                                        }
                                    />
                                    <FormControlLabel
                                        value="Discovery conversations"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                Discovery conversations
                                            </Typography>
                                        }
                                    />

                                    <FormControlLabel
                                        value="Objection handling"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                Objection handling
                                            </Typography>
                                        }
                                    />

                                    <FormControlLabel
                                        value="Follow-ups"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                Follow-ups
                                            </Typography>
                                        }
                                    />

                                    <FormControlLabel
                                        value="Negotiation"
                                        control={<Radio />}
                                        label={
                                            <Typography variant="h6" sx={{ color: "#9e9e9e", fontWeight: 600, fontSize: { xs: "14px", sm: "14px" } }}>
                                                Negotiation
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>

                                <FormHelperText>
                                    {errors.revenueLeak?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />

                </Grid>

                {/* RIGHT SIDE */}
                <Grid item xs={12} md={6}>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                {...field}
                                placeholder="Email"
                                fullWidth
                                error={!!errors.email}
                                helperText={errors.email?.message}
                                sx={{ ...cleanField }}
                            />
                        )}
                    />

                    <Controller
                        name="companyType"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                {...field}
                                placeholder="Company / Independent"
                                fullWidth
                                error={!!errors.companyType}
                                helperText={errors.companyType?.message}
                                sx={{ mt: 2, ...cleanField }}
                            />
                        )}
                    />

                    <Typography fontWeight={600} variant="h6" sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }}>
                        Do you currently have a structured sales process?*
                    </Typography>

                    <Controller
                        name="salesProcess"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.salesProcess}>
                                <RadioGroup {...field}>
                                    <FormControlLabel
                                        value="Yes (Documented & Used)"
                                        control={<Radio />}
                                        label={
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: "#9e9e9e",
                                                    fontWeight: 600,
                                                    fontSize: { xs: "14px", sm: "14px" },
                                                }}
                                            >
                                                Yes (Documented & Used)
                                            </Typography>
                                        }
                                    />

                                    <FormControlLabel
                                        value="Informal"
                                        control={<Radio />}
                                        label={
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: "#9e9e9e",
                                                    fontWeight: 600,
                                                    fontSize: { xs: "14px", sm: "14px" },
                                                }}
                                            >
                                                Informal
                                            </Typography>
                                        }
                                    />

                                    <FormControlLabel
                                        value="No"
                                        control={<Radio />}
                                        label={
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: "#9e9e9e",
                                                    fontWeight: 600,
                                                    fontSize: { xs: "14px", sm: "14px" },
                                                }}
                                            >
                                                No
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>

                                <FormHelperText>
                                    {errors.salesProcess?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />


                    <Controller
                        name="goal"
                        control={control}
                        render={({ field }) => (
                            <BoxInput
                                {...field}
                                placeholder="Primary Goal"
                                multiline
                                rows={2}
                                fullWidth
                                error={!!errors.goal}
                                helperText={errors.goal?.message}
                                sx={{ mt: 2, ...cleanField }}
                            />
                        )}
                    />

                    <Typography
                        fontWeight={600}
                        variant="h6"
                        sx={{ mt: 3, fontSize: { xs: "14px", sm: "16px" } }}
                    >
                        Ready to implement immediately?*
                    </Typography>

                    <Controller
                        name="ready"
                        control={control}
                        render={({ field }) => (
                            <FormControl error={!!errors.ready}>
                                <RadioGroup {...field}>
                                    <FormControlLabel
                                        value="Yes"
                                        control={<Radio />}
                                        label={
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: "#9e9e9e",
                                                    fontWeight: 600,
                                                    fontSize: { xs: "14px", sm: "14px" },
                                                }}
                                            >
                                                Yes
                                            </Typography>
                                        }
                                    />

                                    <FormControlLabel
                                        value="Learning first"
                                        control={<Radio />}
                                        label={
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: "#9e9e9e",
                                                    fontWeight: 600,
                                                    fontSize: { xs: "14px", sm: "14px" },
                                                }}
                                            >
                                                Learning first
                                            </Typography>
                                        }
                                    />
                                </RadioGroup>

                                <FormHelperText>
                                    {errors.ready?.message}
                                </FormHelperText>
                            </FormControl>
                        )}
                    />
                </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={mutation.isPending}
                sx={{
                    py: 2,
                    borderRadius: "16px",
                    background:
                        "linear-gradient(135deg, #0f63a5 0%, #1c88e5 100%)",
                }}
            >
                {mutation.isPending ? (
                    <CircularProgress size={24} />
                ) : (
                    "Register for Masterclass"
                )}
            </Button>
        </Box>
    );
};

export default SalesArchitectureMasterclassForm;